import { SERIES, TRACK, soft, SOFT_FLOOR, LABEL } from './chartColors.js';

// T329 — weekly cohorts as a progression grid instead of a table of counts.
//
// The table said 22 / 14 / 2 / 0 and left the reader to notice that the bottom
// row was four applications sent yesterday. Measured on the live account the
// week this shipped: the newest batch is 4 applications at a median age of 0.9
// days with 0 replies, which a plain grid draws as the worst week on the chart
// — right-censoring rendered as failure, the exact mistake that once made this
// project report "16% vs 46%" for a queue that was merely young.
//
// So an immature cohort is drawn as PROVISIONAL rather than as zero: a dashed,
// unfilled cell and a "maturing" tag. Dropping it instead would hide the
// applications he sent most recently, which is the half he most wants to see —
// the reasoning is already written beside REPLY_MATURITY_DAYS on the server,
// and `mature` is computed there so there is one definition of it.
//
// Intensity is a share of that week's OWN `applied`, not of the largest week.
// A 6-application week where 3 replied is a better week than a 22-application
// week where 4 did, and shading by absolute count would say the opposite.
//
// No hover tooltip: the share is printed in the cell. ChartTooltip positions
// against SVG coordinates, and a number that only exists on hover is a number
// keyboard and touch users do not have.

const STEPS = [
  { key: 'applied', label: 'Applied', tone: SERIES.accent },
  { key: 'replied', label: 'Replied', tone: SERIES.accent },
  { key: 'interviews', label: 'Interviews', tone: SERIES.accent },
  { key: 'offers', label: 'Offers', tone: SERIES.up },
];

/** Share of the cohort that reached this step, or null when the week is empty. */
export function shareOf(cohort, key) {
  if (!cohort?.applied) return null;
  if (key === 'applied') return 1;
  return (cohort[key] ?? 0) / cohort.applied;
}

/** Fill for a share — theme-bound, so it re-tints rather than needing a retarget. */
export function fillFor(share, tone) {
  if (share == null || share <= 0) return TRACK;
  const pct = Math.max(SOFT_FLOOR, Math.round(share * 100));
  return pct >= 100 ? tone : soft(tone, pct);
}

export default function CohortGrid({ cohorts = [], maturityDays = 14 }) {
  const rows = [...cohorts].reverse().filter((c) => c.applied > 0);
  if (!rows.length) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[28rem] border-separate border-spacing-y-1 text-xs">
        <caption className="sr-only">
          Weekly application batches and how far each has progressed. Batches whose
          median age is under {maturityDays} days are marked as still maturing, because
          they have not had time to convert rather than having failed to.
        </caption>
        <thead>
          <tr className="text-left uppercase tracking-wide text-slate-500">
            <th scope="col" className="pb-1 pr-3 font-medium">Week of</th>
            {STEPS.map((s) => (
              <th key={s.key} scope="col" className="pb-1 pr-2 text-center font-medium">{s.label}</th>
            ))}
            <th scope="col" className="pb-1 text-right font-medium">Age</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const immature = c.mature === false;
            return (
              <tr key={c.weekStart}>
                <th scope="row" className="whitespace-nowrap pr-3 text-left font-normal tabular-nums text-slate-400">
                  {c.weekStart}
                </th>
                {STEPS.map((s) => {
                  const share = shareOf(c, s.key);
                  const n = s.key === 'applied' ? c.applied : (c[s.key] ?? 0);
                  return (
                    <td key={s.key} className="pr-2">
                      <div
                        title={
                          `${n} of ${c.applied} ${s.label.toLowerCase()}`
                          + (immature ? ` — this batch is ${c.medianAgeDays}d old, under the ${maturityDays}d reply window` : '')
                        }
                        className={
                          'flex h-8 flex-col items-center justify-center rounded border leading-none '
                          + (immature
                            ? 'border-dashed border-slate-700 text-slate-400'
                            : 'border-slate-800 text-slate-100')
                        }
                        style={{ background: immature ? TRACK : fillFor(share, s.tone) }}
                      >
                        <span className="tabular-nums">{n}</span>
                        {s.key !== 'applied' && (
                          <span className="text-[9px] tabular-nums opacity-70">
                            {share == null ? '—' : `${Math.round(share * 100)}%`}
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="whitespace-nowrap pl-1 text-right tabular-nums" style={{ color: LABEL }}>
                  {c.medianAgeDays == null ? '—' : `${c.medianAgeDays}d`}
                  {immature && (
                    <span className="ml-1 rounded border border-dashed border-slate-700 px-1 text-[10px] text-slate-400">
                      maturing
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
